/**
 * Socket.IO client 배선 — Plan 05-05 Task 1 / WIDGET-07.
 *
 * 역할:
 *   1) `${wsBase}/widget` namespace 에 JWT auth.token + streamerId query 로 접속
 *   2) connect / connect_error / reconnect_attempt 상태 전이 → handlers.onState
 *   3) 5 도메인 이벤트 수신 → Zod parse → handlers.on{Donation,TtsReady,TtsFailed,FilterTriggered,GoalReached}
 *   4) Zod 실패 페이로드는 console.warn 후 drop (apps/realtime 는 listen-only 설계이므로 ack 없음)
 *
 * reconnection 정책:
 *   - reconnectionAttempts: 5 (Plan 05-05 behavior: 5회 실패 후 Socket.IO 가 'reconnect_failed')
 *   - reconnectionDelayMax: 30s
 *   - attemptIn = min(30, 2^n)   (UI 에 다음 시도까지 초 단위 표시 — DESIGN §3.1 #3)
 *
 * T-05-27 완화: audioUrl 검증은 events/handlers.ts 에서 수행 (본 모듈은 payload 전달만).
 */
import { io, type Socket } from 'socket.io-client';
import {
  widgetDonationToastSchema,
  widgetTtsReadySchema,
  widgetTtsFailedSchema,
  widgetFilterTriggeredSchema,
  widgetGoalReachedSchema,
  type WidgetDonationToast,
  type WidgetTtsReady,
  type WidgetTtsFailed,
  type WidgetFilterTriggered,
  type WidgetGoalReached,
} from '@loop/contracts/ws';
import type { WidgetState } from '../states/types.js';

interface ZodIssueLike {
  readonly path: readonly (string | number)[];
  readonly message: string;
}
interface ZodErrorLike {
  readonly issues: readonly ZodIssueLike[];
}
interface SafeParseResult<T> {
  readonly success: boolean;
  readonly data?: T;
  readonly error?: ZodErrorLike;
}
interface SchemaLike<T> {
  safeParse(raw: unknown): SafeParseResult<T>;
}

export interface WidgetSocketHandlers {
  onState(state: WidgetState): void;
  onDonation(payload: WidgetDonationToast): void;
  onTtsReady(payload: WidgetTtsReady): void;
  onTtsFailed(payload: WidgetTtsFailed): void;
  onFilterTriggered(payload: WidgetFilterTriggered): void;
  onGoalReached(payload: WidgetGoalReached): void;
}

export interface ConnectWidgetArgs {
  readonly streamerId: string;
  readonly token: string;
  readonly wsBase: string;
  readonly handlers: WidgetSocketHandlers;
}

export interface WidgetSocketHandle {
  disconnect(): void;
  /**
   * Underlying Socket.IO client — Plan 08-02 game engine adapter 가 추가 리스너 (`donation.completed.v1`) 를
   * 등록할 수 있도록 노출. 기존 호출자는 무시해도 무방 (readonly, BC).
   */
  readonly socket: Socket;
}

function dispatch<T>(
  schema: SchemaLike<T>,
  raw: unknown,
  eventName: string,
  handler: (p: T) => void,
): void {
  const result = schema.safeParse(raw);
  if (!result.success || result.data === undefined) {
    const errors =
      result.error?.issues
        .map((i: ZodIssueLike) => `${i.path.join('.')}: ${i.message}`)
        .join(' · ') ?? 'unknown';
    console.warn(`[widget-socket] ${eventName} payload rejected`, errors);
    return;
  }
  handler(result.data);
}

export function connectWidget(args: ConnectWidgetArgs): WidgetSocketHandle {
  const socket: Socket = io(`${args.wsBase}/widget`, {
    transports: ['websocket'],
    auth: { token: args.token },
    query: { streamerId: args.streamerId },
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelayMax: 30_000,
  });

  socket.on('connect', () => {
    args.handlers.onState({ kind: 'idle' });
  });

  socket.on('connect_error', (err: Error) => {
    const reason = err.message || 'unknown';
    args.handlers.onState({ kind: 'disconnected-fatal', reason });
  });

  socket.io.on('reconnect_attempt', (n: number) => {
    const attemptIn = Math.min(30, 2 ** n);
    args.handlers.onState({ kind: 'reconnecting', attemptIn });
  });

  socket.on('donation.completed.v1', (raw: unknown) => {
    dispatch(widgetDonationToastSchema, raw, 'donation.completed.v1', (p) => {
      args.handlers.onDonation(p);
    });
  });
  socket.on('donation.tts_ready.v1', (raw: unknown) => {
    dispatch(widgetTtsReadySchema, raw, 'donation.tts_ready.v1', (p) => {
      args.handlers.onTtsReady(p);
    });
  });
  socket.on('donation.tts_failed.v1', (raw: unknown) => {
    dispatch(widgetTtsFailedSchema, raw, 'donation.tts_failed.v1', (p) => {
      args.handlers.onTtsFailed(p);
    });
  });
  socket.on('donation.filter_triggered.v1', (raw: unknown) => {
    dispatch(widgetFilterTriggeredSchema, raw, 'donation.filter_triggered.v1', (p) => {
      args.handlers.onFilterTriggered(p);
    });
  });
  socket.on('goal.reached.v1', (raw: unknown) => {
    dispatch(widgetGoalReachedSchema, raw, 'goal.reached.v1', (p) => {
      args.handlers.onGoalReached(p);
    });
  });

  return {
    disconnect: (): void => {
      socket.disconnect();
    },
    socket,
  };
}
